import { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabase";

export interface UseUserResolverReturn {
  resolve: (userId: string | undefined | null) => string | null;
  resolveMany: (userIds: (string | undefined | null)[]) => void;
  getName: (userId: string | undefined | null) => string | null;
  cache: Record<string, string>;
  loading: Record<string, boolean>;
}

/**
 * Resolves user UUIDs to display names.
 * Uses the Supabase profiles table.
 */
export function useUserResolver(): UseUserResolverReturn {
  const [cache, setCache] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const isUuid = (value: string): boolean =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

  const resolve = useCallback((userId: string | undefined | null): string | null => {
    if (!userId) return null;
    return cache[userId] ?? null;
  }, [cache]);

  const resolveMany = useCallback((userIds: (string | undefined | null)[]) => {
    if (!userIds || userIds.length === 0) return;

    const uniqueIds = Array.from(new Set(userIds.filter(Boolean) as string[]))
      .filter((id) => isUuid(id))
      .filter(id => cache[id] === undefined && !loading[id]);

    if (uniqueIds.length === 0) return;

    uniqueIds.forEach(id => {
      setLoading(prev => ({ ...prev, [id]: true }));
    });

    supabase
      .from("profiles")
      .select("id, full_name, first_name, last_name, email")
      .in("id", uniqueIds)
      .then(({ data, error }) => {
        uniqueIds.forEach(id => {
          setLoading(prev => ({ ...prev, [id]: false }));
        });

        if (error) {
          console.error("Failed to resolve user names:", error);
          return;
        }

        const newEntries: Record<string, string> = {};
        (data || []).forEach((profile) => {
          const fullName = profile.full_name?.trim();
          const displayName = fullName || profile.id;
          newEntries[profile.id] = displayName;
        });

        uniqueIds.forEach(id => {
          if (newEntries[id] === undefined) {
            newEntries[id] = id;
          }
        });

        setCache(prev => ({ ...prev, ...newEntries }));
      });
  }, [cache, loading, setCache, setLoading]);

  const getName = useCallback((userId: string | undefined | null): string | null => {
    if (!userId) return null;
    return cache[userId] ?? null;
  }, [cache]);

  const value = useMemo<UseUserResolverReturn>(() => ({
    resolve,
    resolveMany,
    getName,
    cache,
    loading,
  }), [resolve, resolveMany, getName, cache, loading]);

  return value;
}
